import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Gift, Mail, Download, Home, ArrowRight } from 'lucide-react';
import giftCardService from './services/giftCardServices';

const GiftCardSuccessPage = ({ isMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const giftCard = location.state?.giftCard;
  
  // If no gift card data is present, redirect after a delay
  useEffect(() => {
    if (!giftCard) {
      const timer = setTimeout(() => {
        navigate('/gift-cards');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [giftCard, navigate]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Share gift card via email - this would normally call your backend API
  const handleShareViaEmail = () => {
    // In a real app, this would call an API endpoint to send an email
    if (giftCard && giftCard.recipientEmail) {
      fetch('/api/send-gift-card-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          giftCard,
          resend: true
        }),
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          alert('Email has been sent to the recipient.');
        } else {
          alert('There was an issue sending the email. Please try again later.');
        }
      })
      .catch(error => {
        console.error('Error sending email:', error);
        alert('An error occurred while sending the email. Please try again later.');
      });
    } else {
      alert('No recipient email was provided. Please enter an email address when purchasing a gift card to use this feature.');
    }
  };
  
  // Print gift card - in a real app, this would generate a PDF
  const handlePrintGiftCard = () => {
    // You would implement actual PDF generation here
    
    // For now, just use browser print functionality
    window.print();
  };
  
  // If no gift card data, show loading/error state
  if (!giftCard) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 md:pt-20">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3A98B9] mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold mb-4">Loading Gift Card Details...</h1>
            <p className="text-gray-600">
              If you're not redirected automatically, please return to the gift cards page.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pt-16 md:pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] py-6 px-8 text-white">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 mr-4" />
              <div>
                <h1 className="text-2xl font-bold">Gift Card Purchase Successful!</h1>
                <p className="opacity-90">Your transaction has been processed successfully</p>
              </div>
            </div>
          </div>
          
          {/* Gift Card Details */}
          <div className="p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Gift Card Details</h2>
                <p className="text-gray-500 text-sm">
                  {giftCard.code && `Gift Card Code: ${giftCardService.formatCode(giftCard.code)}`}
                </p>
              </div>
              <div className="flex mt-2 sm:mt-0">
                <button
                  onClick={handlePrintGiftCard}
                  className="flex items-center text-[#3A98B9] hover:text-[#2A87A8] mr-4"
                >
                  <Download className="h-5 w-5 mr-1" />
                  <span>Print</span>
                </button>
                
                {giftCard.recipientEmail && (
                  <button
                    onClick={handleShareViaEmail}
                    className="flex items-center text-[#3A98B9] hover:text-[#2A87A8]"
                  >
                    <Mail className="h-5 w-5 mr-1" />
                    <span>Resend Email</span>
                  </button>
                )}
              </div>
            </div>
            
            {/* Gift Card Preview */}
            <div className="border-2 border-gray-200 rounded-xl overflow-hidden mb-8 print:border-2 print:border-black">
              <div className="bg-[#3A98B9]/10 p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-16 h-16 bg-[#3A98B9]/20 rounded-full flex items-center justify-center">
                  <Gift className="h-8 w-8 text-[#3A98B9]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#3A98B9]">JP Auto Detailing</h3>
                  <h4 className="text-xl font-bold text-gray-800">${giftCard.amount.toFixed(2)} Gift Card</h4>
                  <p className="text-gray-600 text-sm">
                    Valid until: {formatDate(giftCard.expiresAt)}
                  </p>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm text-gray-500 mb-1">Code</h4>
                    <div className="bg-gray-100 p-3 rounded-lg font-mono text-lg font-bold text-center">
                      {giftCardService.formatCode(giftCard.code)}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm text-gray-500 mb-1">Recipient</h4>
                    <p className="font-medium">{giftCard.recipientName || 'Not specified'}</p>
                    {giftCard.recipientEmail && (
                      <p className="text-gray-600 text-sm">{giftCard.recipientEmail}</p>
                    )}
                  </div>
                  
                  {giftCard.message && (
                    <div className="md:col-span-2">
                      <h4 className="text-sm text-gray-500 mb-1">Message</h4>
                      <p className="bg-gray-50 p-3 rounded-lg italic">"{giftCard.message}"</p>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200 text-center text-gray-500 text-sm">
                  <p>This gift card is valid for all services at JP Auto Detailing.</p>
                  <p>Present this card or code at time of payment.</p>
                </div>
              </div>
            </div>
            
            {/* Delivery Status */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Delivery Status</h3>
              
              {giftCard.recipientEmail ? (
                <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-green-500 mt-0.5 mr-3" />
                    <div>
                      <p className="text-green-800 font-medium">
                        Gift card has been sent to {giftCard.recipientEmail}
                      </p>
                      <p className="text-green-700 text-sm mt-1">
                        The recipient should receive an email with the gift card details shortly.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                  <div className="flex items-start">
                    <Gift className="h-5 w-5 text-yellow-500 mt-0.5 mr-3" />
                    <div>
                      <p className="text-yellow-800 font-medium">
                        No recipient email provided
                      </p>
                      <p className="text-yellow-700 text-sm mt-1">
                        Please print this page or save the gift card code to share with the recipient.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Instructions */}
            <div className="mb-8 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">How to Redeem</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="bg-[#3A98B9] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">1</span>
                  <span>Visit our website or call to book any detailing service</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-[#3A98B9] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">2</span>
                  <span>During checkout, enter the gift card code</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-[#3A98B9] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0">3</span>
                  <span>The gift card amount will be applied to your total</span>
                </li>
              </ul>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <Link
                to="/"
                className="flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <Home className="h-5 w-5 mr-2" />
                Return to Home
              </Link>
              
              <Link
                to="/book"
                className="flex items-center justify-center bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg"
              >
                Book a Service
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCardSuccessPage;